import { useEffect, useState } from 'react';
import { Table, Grid, GridCol, Group, Loader } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import { fetchDashboardInfo, IDashboardEquipment } from '../../../libraries/dashboard';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { LoginState } from '../../client-redux/login/reducer';
import { ReactComponent as CheckIcon } from '../../../assets/img/check.svg';
import { ReactComponent as XIcon } from '../../../assets/img/x.svg';
import { getLocationName } from '../../utils/script';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import PaginationComponent from '../../components/pagination';
import { useNavigate } from 'react-router-dom';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { ReactComponent as AlertIcon } from '../../../assets/img/triangle-exclamation-solid.svg';
import { EquipState } from '../../../libraries/zps';
import './styles.scss';

const DashboardV2 = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const navigate = useNavigate();
  const [list, setList] = useState<IDashboardEquipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadDashboardInfo();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadDashboardInfo = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);

    fetchDashboardInfo(filterBarData.entity || user.user?.entid || 0, filterBarData.lid || 0)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response.data?.equipments);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .finally(() => {
        setLoading(false);
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleClick = (eid: number) => {
    navigate(`/dashboardV2/equipment/${eid}`);
  };

  return (
    <div className="dashboard">
      <Header pageTitle={lang.DASHBOARD_EQUIPMENT_LIST} />

      <FilterBar
        onSearch={loadDashboardInfo}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.GLOBAL_EQUIPMENT}</Table.Th>
              <Table.Th>{lang.HELPDESK_EQUIPMENT_TYPE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((equipment, index) => {
                    return (
                      <Table.Tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleClick(equipment.eid)}>
                        {/* Delegation */}
                        <Table.Td>{getLocationName(equipment.sid)}</Table.Td>

                        {/* Equip. description */}
                        <Table.Td>
                          {equipment.eid + (equipment.description ? ` - ${equipment.description}` : '')}
                        </Table.Td>

                        {/*Equip. type */}
                        <Table.Td>{equipment.equiType}</Table.Td>

                        {/* Equip status */}
                        <Table.Td>
                          {equipment.state === EquipState.IN_SERVICE ? (
                            <CheckIcon className="status-ok" />
                          ) : equipment.state === EquipState.IN_SERVICE_WITH_WARN ? (
                            <AlertIcon className="alert-icon" />
                          ) : (
                            <XIcon className="status-not-ok" />
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Grid gap="sm" classNames={{ root: 'iconography' }}>
        <GridCol>
          <CheckIcon className="status-ok" />
          <span>{lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}</span>
        </GridCol>

        <GridCol>
          <XIcon className="status-not-ok" />
          <span>{lang.OUT_OF_SERVICE}</span>
        </GridCol>
      </Grid>

      <Group>
        <PaginationComponent
          total={list ? Math.ceil(list.length / limit) : 0}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default DashboardV2;
